import { WithEnterpriseId } from '@mews-commander/types';
import { OverlayedLoader } from '@mews-commander/ui';
import { PageFooter } from '@mews/b2b-ui';
import { LocalizedText, lt, showSuccessAlert } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Button, Container } from '@optimus-web/core';
import { useTargetEnterpriseIdContext } from '../context';
import { useNavigateToSubscriptions } from '../hooks';
import { useIntegrationFormControls, UseIntegrationFormControlsParams } from './useIntegrationFormControls';

export interface IntegrationUpdateFormProps<InputValues extends object, OutputValues extends object = InputValues>
    extends Omit<UseIntegrationFormControlsParams<InputValues, OutputValues>, 'enterpriseId' | 'onSubmit'> {
    handleSubmit: (values: OutputValues & WithEnterpriseId, onSuccess: () => void) => void;
    isLoading?: boolean;
}

export const IntegrationUpdateForm = <InputValues extends object, OutputValues extends object = InputValues>({
    children,
    isLoading,
    handleSubmit,
    ...other
}: React.PropsWithChildren<IntegrationUpdateFormProps<InputValues, OutputValues>>) => {
    const targetEnterpriseId = useTargetEnterpriseIdContext();
    const { navigateToSubscriptions } = useNavigateToSubscriptions();

    const onSuccess = () => {
        showSuccessAlert({
            description: <LocalizedText textKey={TextKey.ChangesSuccessfullySaved} />,
        });

        void navigateToSubscriptions();
    };

    const onSubmit = (values: OutputValues) => {
        handleSubmit(
            {
                ...values,
                enterpriseId: targetEnterpriseId,
            },
            onSuccess
        );
    };

    const { renderFormContent, submit } = useIntegrationFormControls<InputValues, OutputValues>({
        ...other,
        onSubmit,
    });

    return (
        <>
            <Container padding={['0', '0', '1200', '0']}>
                {renderFormContent(children)}
                <PageFooter>
                    <Button variant="tertiary" onClick={navigateToSubscriptions} type="submit">
                        {lt('Cancel')}
                    </Button>
                    <Button variant="primary" onClick={submit} type="submit">
                        {lt('Save')}
                    </Button>
                </PageFooter>
            </Container>
            <OverlayedLoader show={isLoading} fixed />
        </>
    );
};
